<template>
  <article
    ref="root"
    class="max-root max-interface"
    :class="{ 'max-motion': !reducedMotion }"
    :style="rootStyle"
    :lang="lang"
    :data-max-bleed="bleed ? 'true' : undefined"
    :data-max-scheme="scheme"
  >
    <div v-if="!spec" class="max-degraded" role="alert">{{ labels.degraded }}</div>
    <template v-else>
      <header v-if="!hideHeader" class="max-doc__header">
        <div class="max-doc__top">
          <h1 class="max-doc__title">{{ spec.title }}</h1>
          <div v-if="$slots.actions" class="max-doc__actions"><slot name="actions" /></div>
        </div>
        <p v-if="spec.description" class="max-doc__subtitle">{{ spec.description }}</p>
      </header>
      <slot name="notice" />
      <p class="max-interface__status" role="status" aria-live="polite">{{ busy ? labels.ui.working : '' }}</p>
      <div class="max-doc__body" :aria-busy="busy || undefined">
        <MaxNode v-for="(node, i) in body" :key="(node as any).id ?? i" :node="node" :index="i" />
      </div>
    </template>

    <div v-if="panel" class="max-drawer-backdrop" @click.self="closePanel" @keydown.esc.prevent="closePanel">
      <div ref="drawer" role="dialog" aria-modal="true" :aria-labelledby="dialogId + '-panel'" class="max-drawer" :data-size="panel.size">
        <header class="max-drawer__header">
          <h2 :id="dialogId + '-panel'" class="max-drawer__title">{{ panel.title }}</h2>
          <button ref="drawerClose" type="button" class="max-button" data-intent="neutral" data-variant="ghost" data-icon-only="true" :aria-label="labels.close" :title="labels.close" @click="closePanel">
            <MaxIcon name="close" />
          </button>
        </header>
        <div class="max-drawer__body" data-max-layout="stack">
          <MaxNode v-for="(node, i) in panel.body" :key="(node as any).id ?? i" :node="node" :index="i" />
        </div>
      </div>
    </div>

    <div v-if="pending" class="max-dialog-backdrop" @keydown.esc.prevent="close(false)" @keydown.tab="trapFocus">
      <div
        ref="dialog"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="dialogId + '-title'"
        :aria-describedby="pending.message ? dialogId + '-message' : undefined"
        class="max-dialog"
      >
        <h2 :id="dialogId + '-title'" class="max-dialog__title">{{ pending.title }}</h2>
        <p v-if="pending.message" :id="dialogId + '-message'" class="max-dialog__message">{{ pending.message }}</p>
        <div class="max-dialog__actions">
          <button ref="cancelButton" type="button" class="max-button" data-intent="neutral" @click="close(false)">{{ labels.ui.cancel }}</button>
          <button type="button" class="max-button" :data-intent="pending.intent ?? 'accent'" @click="close(true)">{{ pending.confirmLabel || labels.ui.confirm }}</button>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, shallowRef, toRef, useId, watch } from 'vue'
import { presetCssVars, getPreset } from '../core/tokens.ts'
import { mergeLabels, type MaxLabels } from '../core/labels.ts'
import { toBcp47 } from '../core/format.ts'
import { fillDaterangeState, initialState, lintInterface, type MaxInterfaceSpec } from '../core/interface.ts'
import { evalExpr, type ExprUser } from '../core/expr.ts'
import { InterfaceRuntime, type InterfaceHost, type InterfaceUi } from '../core/interface-runtime.ts'
import { BACKGROUND_WINDOW_SECONDS, refreshDelay } from '../core/refresh.ts'
import MaxIcon from './MaxIcon.vue'
import {
  provideMaxContext, provideMaxInterface, useMaxPreset, useReducedMotion,
  type MaxInterfaceHost, type MaxRenderContext
} from '../runtime/context.ts'

const props = withDefaults(defineProps<{
  spec: unknown
  host: MaxInterfaceHost
  labels?: Partial<MaxLabels> | null
  preset?: string | null
  bleed?: boolean
  hideHeader?: boolean
  openEntity?: MaxRenderContext['openEntity']
  canOpenEntity?: MaxRenderContext['canOpenEntity']
  imageUrl?: MaxRenderContext['imageUrl']
  /** Shop time zone for dates and fmt, e.g. Europe/Helsinki; the browser's when left out */
  timeZone?: string | null
  /** The signed-in user, read as user.id and user.name. Display data only, never access control */
  user?: ExprUser | null
}>(), {
  labels: null,
  preset: null,
  bleed: false,
  hideHeader: false,
  openEntity: undefined,
  canOpenEntity: undefined,
  imageUrl: undefined,
  timeZone: null,
  user: null
})

type ConfirmRequest = Parameters<InterfaceHost['confirm']>[0]

const spec = computed(() => lintInterface(props.spec).valid ? props.spec as MaxInterfaceSpec : null)
const labels = computed(() => mergeLabels(props.labels))
const presetId = useMaxPreset(() => props.preset, () => spec.value?.preset)
const locale = computed(() => spec.value?.locale ?? 'en_US')
const lang = computed(() => toBcp47(locale.value))
const scheme = computed(() => getPreset(presetId.value).scheme)
const rootStyle = computed(() => presetCssVars(presetId.value))
const reducedMotion = useReducedMotion()

const running = ref(0)
const busy = computed(() => running.value > 0)
const pending = shallowRef<(ConfirmRequest & { resolve: (ok: boolean) => void }) | null>(null)
const cancelButton = ref<HTMLButtonElement | null>(null)
const dialog = ref<HTMLElement | null>(null)
const dialogId = 'max-d-' + useId()
let returnFocus: HTMLElement | null = null

function inlineConfirm (request: ConfirmRequest): Promise<boolean> {
  return new Promise(resolve => {
    returnFocus = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null
    pending.value = { ...request, resolve }
    nextTick(() => cancelButton.value?.focus())
  })
}

function trapFocus (event: KeyboardEvent) {
  const buttons = Array.from(dialog.value?.querySelectorAll('button') ?? [])
  const first = buttons[0]
  const last = buttons[buttons.length - 1]
  if (!first || !last) return
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function close (ok: boolean) {
  const request = pending.value
  pending.value = null
  request?.resolve(ok)
  returnFocus?.focus?.()
  returnFocus = null
}

async function clipboardCopy (text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

const host: InterfaceHost = {
  call: (name, input, options) => options ? props.host.call(name, input, options) : props.host.call(name, input),
  navigate: (to, id) => props.host.navigate(to, id),
  toast: (message, intent) => props.host.toast(message, intent),
  confirm: request => props.host.confirm ? props.host.confirm(request) : inlineConfirm(request),
  print: (kind, id) => props.host.print?.(kind, id),
  copy: text => props.host.copy ? props.host.copy(text) : clipboardCopy(text)
}

const runtime = shallowRef<InterfaceRuntime | null>(null)
const ui = reactive<InterfaceUi>({ panel: null })
const drawer = ref<HTMLElement | null>(null)
const drawerClose = ref<HTMLButtonElement | null>(null)
let panelReturnFocus: HTMLElement | null = null
let generation = 0

// ---------------------------------------------------------------------------
// Background reruns of on.load: spec.refresh and refreshInBackground() (a change saved elsewhere).
// One at a time, at least BACKGROUND_GAP_MS after the previous one ended (the backend allows one
// run per BACKGROUND_WINDOW_SECONDS), and only while the tool is in view and nobody is using it.

/** A change saved elsewhere often comes with more: wait this long so one reload covers them */
const CHANGE_SETTLE_MS = 2000
const BACKGROUND_GAP_MS = (BACKGROUND_WINDOW_SECONDS + 1) * 1000
/** How often a blocked background run checks again whether the tool is free */
const BLOCKED_RETRY_MS = 5000

const root = ref<HTMLElement | null>(null)
let backgroundTimer: ReturnType<typeof setTimeout> | null = null
let pollTimer: ReturnType<typeof setTimeout> | null = null
let backgroundRunning = false
let backgroundEnded = 0

/** The tool is on screen and idle: no run, dialog or panel open and no field being edited */
function idle (): boolean {
  if (!runtime.value || running.value > 0 || backgroundRunning || pending.value || ui.panel) return false
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return false
  const el = root.value
  // A POS tab that is not shown has no layout boxes
  if (!el || !el.getClientRects().length) return false
  const active = document.activeElement as HTMLElement | null
  if (active && el.contains(active) && (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) return false
  return true
}

function clearBackground () {
  if (backgroundTimer) clearTimeout(backgroundTimer)
  if (pollTimer) clearTimeout(pollTimer)
  backgroundTimer = pollTimer = null
}

function requestBackground (delay: number) {
  if (backgroundTimer || !runtime.value) return
  // After a rate_limited refusal the tool waits out its back-off first
  const at = Math.max(Date.now() + delay, backgroundEnded + BACKGROUND_GAP_MS, Date.now() + runtime.value.backedOffFor())
  backgroundTimer = setTimeout(runBackground, at - Date.now())
}

async function runBackground () {
  backgroundTimer = null
  const current = runtime.value
  if (!current) return
  // A change that came in while a run was going waits a full gap after that run
  const wait = Math.max(backgroundEnded + BACKGROUND_GAP_MS - Date.now(), current.backedOffFor())
  if (backgroundRunning || wait > 0) {
    backgroundTimer = setTimeout(runBackground, backgroundRunning ? BLOCKED_RETRY_MS : wait)
    return
  }
  if (!idle()) {
    backgroundTimer = setTimeout(runBackground, BLOCKED_RETRY_MS)
    return
  }
  const at = generation
  backgroundRunning = true
  try {
    await current.loadInBackground()
  } finally {
    if (at === generation) {
      backgroundRunning = false
      backgroundEnded = Date.now()
    }
  }
}

function schedulePoll () {
  const seconds = spec.value?.refresh
  if (!seconds) return
  pollTimer = setTimeout(() => {
    requestBackground(0)
    schedulePoll()
  }, refreshDelay(seconds))
}

function start () {
  if (pending.value) close(false)
  generation++
  clearBackground()
  backgroundRunning = false
  ui.panel = null
  const env = props.timeZone ? { timeZone: props.timeZone } : {}
  let state: Record<string, unknown> | null = null
  if (spec.value) {
    state = initialState(spec.value)
    // A daterange filter shows its preset's dates from the start; fill them before on.load runs
    fillDaterangeState(spec.value, state, String(evalExpr({ today: [] }, {}, env)))
  }
  runtime.value = spec.value && state
    ? new InterfaceRuntime(spec.value, reactive(state), host, { ui, env, user: () => props.user })
    : null
  if (runtime.value) {
    load()
    schedulePoll()
  }
}

async function run (actions: unknown, extra: Record<string, unknown> = {}) {
  const current = runtime.value
  if (!current || !Array.isArray(actions) || !actions.length) return
  await track(current, () => current.run(actions, extra))
}

/** on.load: when the tool opens and from the refresh button; the runtime coalesces runs (JDEV-3307) */
async function load () {
  const current = runtime.value
  if (!current || !spec.value?.on?.load?.length) return
  await track(current, () => current.load())
}

async function track (current: InterfaceRuntime, fn: () => Promise<void>) {
  const at = generation
  running.value++
  try {
    await fn()
  } catch {
    if (at === generation && runtime.value === current) host.toast(labels.value.ui.failed, 'negative')
  } finally {
    running.value--
  }
}

const body = computed(() => runtime.value?.resolve() ?? [])
const panel = computed(() => ui.panel ? runtime.value?.resolvePanel() ?? null : null)

function closePanel () {
  ui.panel = null
}

watch(() => ui.panel, (open, was) => {
  if (open && !was) {
    panelReturnFocus = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null
    nextTick(() => drawerClose.value?.focus())
  } else if (!open && was) {
    panelReturnFocus?.focus?.()
    panelReturnFocus = null
  }
})

provideMaxContext({
  get labels () { return labels.value },
  get locale () { return locale.value },
  get preset () { return presetId.value },
  reducedMotion: toRef(reducedMotion),
  get openEntity () { return props.openEntity },
  get canOpenEntity () { return props.canOpenEntity },
  get imageUrl () { return props.imageUrl },
  get icons () { return spec.value?.icons }
} as MaxRenderContext)

provideMaxInterface({
  busy,
  run,
  setState: (name, value) => runtime.value?.setState(name, value),
  getState: name => runtime.value?.state[name],
  setIn: (name, key, value) => runtime.value?.setIn(name, key, value),
  setSelection: (name, keys, rows, rowKey) => runtime.value?.setSelection(name, keys, rows, rowKey),
  canSearchList: name => runtime.value?.canSearchList(name) ?? false,
  searchList: (name, query, offset) => runtime.value?.searchList(name, query, offset) ?? Promise.resolve({ count: 0, more: false })
})

onMounted(start)
watch(spec, start)
onBeforeUnmount(() => {
  generation++
  clearBackground()
  if (pending.value) close(false)
})

defineExpose({
  reload: load,
  /** Data the tool reads was changed elsewhere: rerun on.load in the background once it is free */
  refreshInBackground: () => requestBackground(CHANGE_SETTLE_MS)
})
</script>
